import React, { useState, useEffect, useRef } from "react";
import useData from "../lib/useData";
import {
  categorie,
  priorita,
  stati,
  ticketService,
  tipo,
} from "../services/ticketService";
import { ChevronLeft, ShieldCheck } from "lucide-react";
import { PriorityBadge, StatusBadge } from "../components/Layout";
import { formatDate } from "../lib/utils";
import {
  Box,
  Typography,
  Button,
  Paper,
  MenuItem,
  Select,
  IconButton,
  Avatar,
  Divider,
  useMediaQuery,
  Card,
  CardContent,
  Grid,
  Chip,
  CardHeader,
} from "@mui/material";
import { spacing, useTheme } from "@mui/system";
import ChatComponent from "../components/ChatComponent";
import styled from "@emotion/styled";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../../../../redux/slices/snackbar";
import { API_BASE_URL } from "../../../../config";
import ClosePopup from "../components/ClosePopup";
import { set } from "date-fns";

const Spacer = styled.div(spacing);

export default function TicketDetail() {
  const filters = useSelector((state) => state.issueFilters);
  const navigate = useNavigate();
  const {
    ticket,
    fetchTicketDetails,
    handleUpdateField: updateField,
  } = useData(false); // Pass false for detail view

  const { idUid } = useParams();
  const [dataTicket, setDataTicket] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [note, setNote] = useState("");

  const dispatch = useDispatch();

  const cOpt = dataTicket?.cOpt;
  const cMess = dataTicket?.cMess;

  const canEditSelects = cOpt === 1;
  const canSendMessages = cMess === 1;

  const handleShowSnackbar = (message, severity) => {
    dispatch(showSnackbar({ message, severity }));
  };

  useEffect(() => {
    if (ticket) {
      setDataTicket(ticket);
    }
  }, [ticket]);

  useEffect(() => {
    if (idUid) {
      fetchTicketDetails(idUid, navigate);
    }
  }, [idUid, fetchTicketDetails]);

  const scrollRef = useRef(null);

  const users = {
    andrearotaru: "Andrea Rotaru",
    vincenzo: "Vincenzo",
  };

  const detailsTicket = (ticket) => {
    return (
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Details
          </Typography>

          <Spacer mb={4} />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              fontSize: "0.875rem",
              fontWeight: 500,
            }}
          >
            {/* Priority */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography component="span" sx={{ ml: 2 }}>
                Priorità
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {canEditSelects ? (
                  <Select
                    size="small"
                    value={dataTicket.idPriorita || ""}
                    onChange={(e) =>
                      handleChangeFields("idPriorita", e.target.value)
                    }
                    sx={{ minWidth: 100, height: 24 }}
                  >
                    {filters?.options
                      .find((option) => option.label === "Priorità")
                      ?.dataset?.map((p) => (
                        <MenuItem key={p.value} value={p.value}>
                          <Typography component="span">{p.label}</Typography>
                        </MenuItem>
                      ))}
                  </Select>
                ) : (
                  <PriorityBadge
                    priority={
                      filters?.options
                        .find((option) => option.label === "Priorità")
                        ?.dataset?.find(
                          (option) =>
                            option.value === String(ticket.idPriorita),
                        )?.label
                    }
                  />
                )}
              </Box>
            </Box>

            {/* Status */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography component="span" sx={{ ml: 2 }}>
                Stato
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {canEditSelects ? (
                  <Select
                    size="small"
                    value={dataTicket.idStato || ""}
                    onChange={(e) =>
                      handleChangeFields("idStato", e.target.value)
                    }
                    sx={{ minWidth: 100, height: 24 }}
                  >
                    {filters?.options
                      .find((option) => option.label === "Stato")
                      ?.dataset?.map((s) => (
                        <MenuItem key={s.value} value={s.value}>
                          {s.label}
                        </MenuItem>
                      ))}
                  </Select>
                ) : (
                  <StatusBadge
                    status={
                      filters?.options
                        .find((option) => option.label === "Stato")
                        ?.dataset?.find(
                          (option) => option.value === String(ticket.idStato),
                        )?.label
                    }
                  />
                )}
              </Box>
            </Box>

            {/* Category */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography component="span" sx={{ ml: 2 }}>
                Categoria
              </Typography>
              {canEditSelects ? (
                <Select
                  size="small"
                  value={dataTicket.idCategoria || ""}
                  onChange={(e) =>
                    handleChangeFields("idCategoria", e.target.value)
                  }
                  sx={{ minWidth: 120, height: 24 }}
                >
                  {filters?.options
                    .find((option) => option.label === "Categoria")
                    ?.dataset?.map((c) => (
                      <MenuItem key={c.value} value={c.value}>
                        {c.label}
                      </MenuItem>
                    ))}
                </Select>
              ) : (
                <Typography component="span">
                  {
                    filters?.options
                      .find((option) => option.label === "Categoria")
                      ?.dataset?.find(
                        (option) => option.value === String(ticket.idCategoria),
                      )?.label
                  }
                </Typography>
              )}
            </Box>

            {/* Type */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography component="span" sx={{ ml: 2 }}>
                Tipo
              </Typography>
              {canEditSelects ? (
                <Select
                  size="small"
                  value={dataTicket.idTipo || ""}
                  onChange={(e) => handleChangeFields("idTipo", e.target.value)}
                  sx={{ minWidth: 120, height: 24 }}
                >
                  {filters?.options
                    .find((option) => option.label === "Tipo")
                    ?.dataset?.map((t) => (
                      <MenuItem key={t.value} value={t.value}>
                        {t.label}
                      </MenuItem>
                    ))}
                </Select>
              ) : (
                <Typography component="span">
                  {
                    filters?.options
                      .find((option) => option.label === "Tipo")
                      ?.dataset?.find(
                        (option) => option.value === String(ticket.idTipo),
                      )?.label
                  }
                </Typography>
              )}{" "}
            </Box>

            {/* Assigned to */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography component="span" sx={{ ml: 2 }}>
                Assegnato a
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography component="span">Vincenzo</Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
        <Divider />
      </Card>
    );
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [ticket?.messages]);

  const handleSendMessage = async (content) => {
    if (!content.trim() || !idUid) return;

    console.log(content);

    /* try {
      await sendMsg(idUid, content);
    } catch (error) {
      console.error("Failed to send message:", error);
    } */
  };

  const handleSaveTicket = async () => {
    dataTicket.closeNote = note; // Set noteChiusura to null if empty, otherwise use the note value

    console.log(dataTicket);
    const response = await ticketService.handleUpdateTicket(dataTicket);

    if (response.status === -1) {
      sessionStorage.clear();
      /*  navigate("/auth/sign-in"); */
    } else if (response.status === 0) {
      handleShowSnackbar(response.message, "error");
      /* navigate("/private"); */
    } else {
      handleShowSnackbar(response.message, "success");
      fetchTicketDetails(idUid, navigate);
    }
    handleClosePopup();
  };

  const handleChangeFields = (field, value) => {
    setDataTicket((prev) => ({
      ...prev,
      [field]: parseInt(value),
    }));
  };

  const handleClosePopup = () => {
    setOpenDialog(false);
    setNote("");
  };

  if (!ticket)
    return <Box sx={{ p: 5, textAlign: "center" }}>Caricamento... {idUid}</Box>;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        mt: -1,
      }}
    >
      <ClosePopup
        open={openDialog}
        handleClosePopup={handleClosePopup}
        handleSave={handleSaveTicket}
        note={note}
        setNote={setNote}
      />
      <Box
        component="header"
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0,
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "start",
            gap: 2,
            width: "100%",
          }}
        >
          <IconButton
            onClick={() => navigate("/pages/supporto")}
            sx={{
              bgcolor: "transparent",
            }}
          >
            <ChevronLeft size={24} />
          </IconButton>
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: isMobile ? "start" : "center",
                flexDirection: isMobile ? "column" : "row",
                gap: 1.5,
                mb: 0.5,
              }}
            >
              <Box
                sx={{
                  fontWeight: "bold",
                  color: "#223354",
                  bgcolor: "#f1f5f9",
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  textTransform: "uppercase",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    letterSpacing: "-0.025em",
                  }}
                >
                  {ticket.codice}
                </Typography>
              </Box>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  letterSpacing: "-0.025em",
                }}
              >
                {ticket.titolo}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="contained"
            disabled={!canEditSelects}
            onClick={() =>
              dataTicket.idStato === 5
                ? setOpenDialog(true)
                : handleSaveTicket()
            }
          >
            Salva
          </Button>
        </Box>
      </Box>
      <Grid container spacing={6}>
        <Grid item xs={12} lg={3} xl={3}>
          <Card sx={{ display: { xl: "block" } }}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Caller
                </Typography>

                <Spacer mb={4} />

                <Grid container direction="row" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "#f1f5f9",
                          color: "#475569",
                          fontWeight: "bold",
                          fontSize: "0.875rem",
                        }}
                      >
                        M
                        {/* {ticket?.customerName.substring(0, 2).toUpperCase()} */}
                      </Avatar>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: "bold",
                          }}
                        >
                          {/* {ticket?.customerName} */} Mario Rossi
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: "#64748b",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: 160,
                          }}
                        >
                          {/* {ticket?.customerEmail} */}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </CardContent>
              <Divider />
            </Card>

            {<>{detailsTicket(ticket)}</>}

            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Date
                </Typography>

                <Spacer mb={4} />

                <Grid container direction="row" alignItems="center" mb={2}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography component="span" sx={{ ml: 2 }}>
                        Data Apertura
                      </Typography>
                      <Typography component="span">
                        {formatDate(ticket.dataApertura)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography component="span" sx={{ ml: 2 }}>
                        Data Chiusura
                      </Typography>
                      <Typography component="span">
                        {formatDate(ticket.dataChiusura)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography component="span" sx={{ ml: 2 }}>
                        Ultimo Aggiornamento
                      </Typography>
                      <Typography component="span">
                        {formatDate(ticket.dataUltimoAggiornamento)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </CardContent>
            </Card>
          </Card>
        </Grid>
        <Grid item xs={12} lg={3} xl={4}>
          <Card>
            <Card>
              <CardContent>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      mb: 4,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Summary
                  </Typography>
                  <Typography sx={{ ml: 2 }}>{ticket.titolo}</Typography>
                </Box>
              </CardContent>
              <Divider />
            </Card>
            <Card>
              <CardContent>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: "bold",
                      mb: 4,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    Description
                  </Typography>
                  <Typography sx={{ ml: 2 }}>{ticket.descrizione}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Card>
          <Card sx={{ mt: 4 }}>
            <CardContent>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                    mb: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  Close Note
                </Typography>
                <Typography sx={{ ml: 2 }}>
                  {ticket.notaChiusura ?? ""}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={6} xl={5}>
          <Card>
            <CardHeader
              title={
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  Work Notes
                </Typography>
              }
            />
            <CardContent>
              <ChatComponent canSendMessages={canSendMessages} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
